import React, { useMemo, useState, useEffect } from 'react';
import { Box, Stack, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Footer from '../Layouts/Footer.jsx';
import { getSession } from '../../utils/authUtils';
import { markOnboardingComplete, hasCompletedOnboarding } from '../../utils/onboardingUtils';
import ProfileStrengthOnboardingStep from './ProfileStrengthOnboardingStep';

const PageWrapper = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f4f6fa',
}));

const HeaderBar = styled(Box)(() => ({
  backgroundColor: '#0b91d6',
  padding: '24px 48px',
  display: 'flex',
  alignItems: 'center',
}));

const Logo = styled('img')(() => ({
  height: 38,
}));

const ContentSection = styled(Box)(() => ({
  flex: 1,
  width: '100%',
  maxWidth: 960,
  margin: '40px auto 72px',
  padding: '0 24px',
}));

const ProgressBar = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: 16,
  marginBottom: 40,
}));

const ProgressSegment = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'completed'
})(({ active, completed }) => ({
  height: 6,
  borderRadius: 999,
  backgroundColor: completed ? '#0b91d6' : active ? '#76c8f0' : '#dbe6f3',
  transition: 'background-color 0.3s ease',
}));

const Card = styled(Box)(() => ({
  backgroundColor: '#ffffff',
  borderRadius: 24,
  padding: '48px 64px',
  boxShadow: '0 30px 70px rgba(15, 42, 74, 0.08)',
  display: 'grid',
  justifyItems: 'center',
  textAlign: 'center',
  gap: 32,
}));

const Illustration = styled('img')(() => ({
  width: '100%',
  maxWidth: 340,
}));

const PrimaryButton = styled(Button)(() => ({
  backgroundColor: '#0b91d6',
  color: '#ffffff',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: 15,
  borderRadius: 8,
  padding: '12px 32px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#0980c0',
  },
}));

const LinkButton = styled(Button)(() => ({
  color: '#ef232a',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
}));

const stepsConfig = [
  {
    id: 'welcome',
    title: 'Welcome to Visibeen!',
    description: 'Your all-in-one SEO companion is here! Track rankings, boost visibility, and grow your online presence smarter, faster.',
    illustration: '/images/feature-illustration.svg',
    primaryLabel: 'Get Started',
    secondaryLabel: 'Skip',
  },
  {
    id: 'visibility',
    title: 'Detailed Competitor Analysis',
    description: 'Track trends, get actionable tips, and stay ahead of the competition with smart recommendations.',
    illustration: '/images/dashboard1.png',
    primaryLabel: 'Continue',
  },
  {
    id: 'tasks',
    title: 'AI-Powered Recommendations',
    description: 'From keyword research to site audits, automate the heavy lifting. Focus on results, not complexity.',
    illustration: '/images/dashboard2.png',
    primaryLabel: 'Start Optimizing',
  },
  {
    id: 'performance',
    title: 'Review & Post Performance Tracking',
    description: 'Get real-time data on keywords, rankings, and competitors so you know what works and what needs attention.',
    illustration: '/images/dashboard3.png',
    primaryLabel: 'Explore Analytics',
  },
  {
    id: 'profile-setup',
    title: 'Just one step',
    description: "Let's analyse the strength of your profile.",
    isForm: true,
    primaryLabel: 'Continue',
  },
];

const NewUserOnboarding = () => {
  const navigate = useNavigate();
  const session = useMemo(() => getSession(), []);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (!session) {
      navigate('/login', { replace: true });
      return;
    }
    if (hasCompletedOnboarding(session)) {
      navigate('/dashboard', { replace: true });
    }
  }, [session, navigate]);

  const handlePrimary = () => {
    if (activeStep >= stepsConfig.length - 1) return;
    setActiveStep((prev) => Math.min(prev + 1, stepsConfig.length - 1));
  };

  const handleSkip = () => {
    markOnboardingComplete(session);
    navigate('/dashboard', { replace: true });
  };

  const currentStep = stepsConfig[activeStep];

  return (
    <PageWrapper>
      <HeaderBar>
        <Logo src={process.env.PUBLIC_URL + '/images/VisibeenLogo12.png'} alt="Visibeen" />
      </HeaderBar>

      <ContentSection>
        <ProgressBar>
          {stepsConfig.map((step, index) => (
            <ProgressSegment
              key={step.id}
              active={index === activeStep}
              completed={index < activeStep}
            />
          ))}
        </ProgressBar>

        {currentStep.isForm ? (
          <ProfileStrengthOnboardingStep
            buttonLabel={currentStep.primaryLabel}
            onComplete={() => {
              markOnboardingComplete(session);
            }}
          />
        ) : (
          <Card>
            {currentStep.illustration && (
              <Illustration src={currentStep.illustration} alt={currentStep.title} />
            )}
            <Stack spacing={1}>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 28,
                  fontWeight: 700,
                  color: '#0b91d6',
                }}
              >
                {currentStep.title}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 16,
                  color: '#475467',
                  maxWidth: 520,
                }}
              >
                {currentStep.description}
              </Typography>
            </Stack>
            <Stack spacing={1} alignItems="center">
              <PrimaryButton onClick={handlePrimary}>{currentStep.primaryLabel}</PrimaryButton>
              {currentStep.secondaryLabel && (
                <LinkButton onClick={handleSkip}>{currentStep.secondaryLabel}</LinkButton>
              )}
            </Stack>
          </Card>
        )}
      </ContentSection>

      <Footer />
    </PageWrapper>
  );
};

export default NewUserOnboarding;
